import { useEffect, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { ChevronsLeft, ChevronsRight, MessagesSquare } from 'lucide-react'

import { cn } from '../../lib/utils'
import { BREAKPOINT_QUERIES, useMediaQuery } from '../../lib/use-media-query'
import { designPixelsToRem, getRootFontScale } from '../../lib/ui-density'
import { ConversationList } from '../../components/Chat/ConversationList'
import type { CurrentIngestDescriptor } from '../../components/Chat/CurrentIngestEntry'
import {
  getPersistedChatSidePanelExpanded,
  getPersistedChatSidePanelWidth,
  setPersistedChatSidePanelExpanded,
  setPersistedChatSidePanelWidth
} from '../../components/Chat/chat-panel-persistence'
import { Button } from '../../components/ui/button'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger
} from '../../components/ui/tooltip'
import type { useOrchestratorChatController } from '../../components/Chat/useOrchestratorChatController'

type OrchestratorChatController = ReturnType<typeof useOrchestratorChatController>

/**
 * Single right-hand slot on the chat page. Phase 1 ships the conversations
 * view; Phase 6 adds an artifact view and a back link into the same slot.
 */
export type ChatSidePanelView = 'conversations' | 'artifact'

export const CHAT_SIDE_PANEL_MIN_WIDTH = 280
export const CHAT_SIDE_PANEL_MAX_WIDTH = 480
export const CHAT_SIDE_PANEL_DEFAULT_WIDTH = 280

export function clampChatSidePanelWidth(value: number): number {
  return Math.min(CHAT_SIDE_PANEL_MAX_WIDTH, Math.max(CHAT_SIDE_PANEL_MIN_WIDTH, value))
}

/** Dragging the left edge leftward widens a right-hand panel. */
export function chatSidePanelWidthAfterDrag(
  startWidth: number,
  startX: number,
  clientX: number,
  rootFontScale = 1
): number {
  return clampChatSidePanelWidth(startWidth + (startX - clientX) / rootFontScale)
}

function shouldDefaultExpanded(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return true
  }
  return window.matchMedia(BREAKPOINT_QUERIES.xl).matches
}

export function ChatSidePanel({
  chat,
  view = 'conversations',
  onSelect,
  currentIngest
}: {
  chat: OrchestratorChatController
  view?: ChatSidePanelView
  onSelect: (id: string) => void
  currentIngest?: CurrentIngestDescriptor
}) {
  const isXl = useMediaQuery(BREAKPOINT_QUERIES.xl)
  const [expanded, setExpanded] = useState<boolean>(() => {
    const persisted = getPersistedChatSidePanelExpanded()
    return persisted !== null ? persisted : shouldDefaultExpanded()
  })
  const [width, setWidth] = useState<number>(() => {
    const persisted = getPersistedChatSidePanelWidth()
    return persisted !== null ? clampChatSidePanelWidth(persisted) : CHAT_SIDE_PANEL_DEFAULT_WIDTH
  })
  const [isResizing, setIsResizing] = useState(false)
  const resizeStartRef = useRef({ x: 0, width: CHAT_SIDE_PANEL_DEFAULT_WIDTH })

  // Viewport is only the default before the first manual toggle. After that
  // the persisted choice wins, including across remounts.
  useEffect(() => {
    if (getPersistedChatSidePanelExpanded() !== null) return
    setExpanded(isXl)
  }, [isXl])

  useEffect(() => {
    setPersistedChatSidePanelWidth(width)
  }, [width])

  useEffect(() => {
    if (!isResizing) return
    const onMove = (event: PointerEvent): void => {
      setWidth(
        chatSidePanelWidthAfterDrag(
          resizeStartRef.current.width,
          resizeStartRef.current.x,
          event.clientX,
          getRootFontScale()
        )
      )
    }
    const onUp = (): void => {
      setIsResizing(false)
    }
    window.addEventListener('pointermove', onMove)
    window.addEventListener('pointerup', onUp)
    window.addEventListener('pointercancel', onUp)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('pointerup', onUp)
      window.removeEventListener('pointercancel', onUp)
    }
  }, [isResizing])

  const toggleExpanded = (): void => {
    setExpanded((current) => {
      const next = !current
      setPersistedChatSidePanelExpanded(next)
      return next
    })
  }

  const handleResizePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault()
    resizeStartRef.current = { x: event.clientX, width }
    setIsResizing(true)
  }

  return (
    <aside
      aria-label="Conversations"
      style={expanded ? { width: designPixelsToRem(width) } : undefined}
      className={cn(
        'relative h-full shrink-0 overflow-hidden border-l bg-background',
        !isResizing && 'transition-[width] duration-300 ease-in-out motion-reduce:transition-none',
        !expanded && 'w-12'
      )}
    >
      {expanded ? (
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize conversations"
          aria-valuemin={CHAT_SIDE_PANEL_MIN_WIDTH}
          aria-valuemax={CHAT_SIDE_PANEL_MAX_WIDTH}
          aria-valuenow={width}
          className="group/resize absolute top-0 bottom-0 left-0 z-40 w-1.5 cursor-col-resize select-none"
          onPointerDown={handleResizePointerDown}
        >
          <div
            className={cn(
              'h-full w-full transition-colors',
              isResizing ? 'bg-border' : 'group-hover/resize:bg-border'
            )}
          />
        </div>
      ) : null}

      <div
        className={cn(
          'absolute inset-0 flex flex-col transition-opacity duration-200 ease-in-out motion-reduce:transition-none',
          expanded ? 'opacity-100' : 'pointer-events-none opacity-0'
        )}
        aria-hidden={!expanded}
      >
        <header className="flex items-center justify-between gap-2 border-b px-3 py-2.5">
          <h2 className="m-0 text-10 font-semibold tracking-[0.1em] text-muted-foreground uppercase">
            Conversations
          </h2>
          <TooltipProvider delayDuration={700}>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Collapse conversations"
                  onClick={toggleExpanded}
                >
                  <ChevronsRight className="size-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">Collapse conversations</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </header>
        {view === 'artifact' ? null : (
          <ConversationList
            conversations={chat.conversations}
            conversationsLoaded={chat.conversationsLoaded}
            activeTabId={chat.activeTabId}
            openTabIds={chat.openTabs}
            onSelect={onSelect}
            onRename={chat.handleRename}
            onDelete={chat.handleRequestDelete}
            {...(currentIngest ? { currentIngest } : {})}
          />
        )}
      </div>

      <button
        type="button"
        title="Open conversations"
        className={cn(
          'group absolute inset-0 flex w-full cursor-pointer flex-col items-center justify-between py-4 transition-opacity duration-200 ease-in-out hover:bg-accent focus-visible:ring-[3px] focus-visible:ring-ring/50 focus-visible:outline-none motion-reduce:transition-none',
          expanded ? 'pointer-events-none opacity-0' : 'opacity-100'
        )}
        aria-label="Open conversations"
        aria-hidden={expanded}
        tabIndex={expanded ? -1 : 0}
        onClick={toggleExpanded}
      >
        <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm">
          <MessagesSquare className="size-3.5" />
        </span>
        <span
          aria-hidden
          className="rotate-180 text-11 font-semibold tracking-[0.2em] text-muted-foreground uppercase transition-colors [writing-mode:vertical-rl] group-hover:text-foreground"
        >
          Conversations
        </span>
        <ChevronsLeft className="size-4 text-muted-foreground transition-colors group-hover:text-foreground" />
      </button>
    </aside>
  )
}
